/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Safe category for the latest observed ingest error.
 */
export const LastErrorKind = {
  AuthFailure: "auth_failure",
  DecodeFailure: "decode_failure",
  LimitExceeded: "limit_exceeded",
} as const;
/**
 * Safe category for the latest observed ingest error.
 */
export type LastErrorKind = ClosedEnum<typeof LastErrorKind>;

/**
 * Derived health of a LiteLLM integration's latest ingest activity.
 */
export const LiteLLMInstanceDiagnosticsStatus = {
  Pending: "pending",
  Success: "success",
  Failed: "failed",
} as const;
/**
 * Derived health of a LiteLLM integration's latest ingest activity.
 */
export type LiteLLMInstanceDiagnosticsStatus = ClosedEnum<
  typeof LiteLLMInstanceDiagnosticsStatus
>;

/**
 * Health and identity-attribution diagnostics for a LiteLLM integration. No prompt, credential, or identity values are returned.
 */
export type LiteLLMInstanceDiagnostics = {
  lastErrorAt?: Date | undefined;
  /**
   * Safe category for the latest observed ingest error.
   */
  lastErrorKind?: LastErrorKind | undefined;
  lastGuardrailEventAt?: Date | undefined;
  lastOtelEventAt?: Date | undefined;
  /**
   * Percentage of model requests in the last 24 hours that resolved to a Speakeasy user.
   */
  platformUserPct24h?: number | undefined;
  reportedLitellmVersion?: string | undefined;
  /**
   * Derived health of a LiteLLM integration's latest ingest activity.
   */
  status: LiteLLMInstanceDiagnosticsStatus;
  /**
   * Percentage of model requests in the last 24 hours that supplied a virtual-key email.
   */
  virtualKeyEmailPct24h?: number | undefined;
};

/** @internal */
export const LastErrorKind$inboundSchema: z.ZodMiniEnum<typeof LastErrorKind> =
  z.enum(LastErrorKind);

/** @internal */
export const LiteLLMInstanceDiagnosticsStatus$inboundSchema: z.ZodMiniEnum<
  typeof LiteLLMInstanceDiagnosticsStatus
> = z.enum(LiteLLMInstanceDiagnosticsStatus);

/** @internal */
export const LiteLLMInstanceDiagnostics$inboundSchema: z.ZodMiniType<
  LiteLLMInstanceDiagnostics,
  unknown
> = z.pipe(
  z.object({
    last_error_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_error_kind: z.optional(LastErrorKind$inboundSchema),
    last_guardrail_event_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_otel_event_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    platform_user_pct_24h: z.optional(z.number()),
    reported_litellm_version: z.optional(z.string()),
    status: LiteLLMInstanceDiagnosticsStatus$inboundSchema,
    virtual_key_email_pct_24h: z.optional(z.number()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "last_error_at": "lastErrorAt",
      "last_error_kind": "lastErrorKind",
      "last_guardrail_event_at": "lastGuardrailEventAt",
      "last_otel_event_at": "lastOtelEventAt",
      "platform_user_pct_24h": "platformUserPct24h",
      "reported_litellm_version": "reportedLitellmVersion",
      "virtual_key_email_pct_24h": "virtualKeyEmailPct24h",
    });
  }),
);

export function liteLLMInstanceDiagnosticsFromJSON(
  jsonString: string,
): SafeParseResult<LiteLLMInstanceDiagnostics, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LiteLLMInstanceDiagnostics$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LiteLLMInstanceDiagnostics' from JSON`,
  );
}
